class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // ── DJ: Force "In stock" when a primary pill filter is active ──
  static hasAutoAvailabilityFacet(params) {
    return (
      params.has('filter.p.product_type') ||
      params.has('filter.v.option.size') ||
      params.has('filter.v.option.color') ||
      params.has('filter.v.option.colour') ||
      params.has('filter.p.vendor') ||
      params.has('filter.v.vendor') ||
      params.has('filter.p.m.vendor')
    );
  }

  static enforceAutoAvailability(searchParams = '') {
    const params = new URLSearchParams(searchParams);
    const shouldAutoFilterInStock = FacetFiltersForm.hasAutoAvailabilityFacet(params);

    // Avoid duplicate availability params.
    params.delete('filter.v.availability');

    // Only auto-apply in-stock when product type, size, or colour is active.
    if (shouldAutoFilterInStock) {
      params.set('filter.v.availability', '1');
    }

    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // ── DJ: Availability must be enforced before fetch/cache/url update ──
    searchParams = FacetFiltersForm.enforceAutoAvailability(searchParams || '');

    FacetFiltersForm.searchParamsPrev = searchParams;
    window.DJ_LAST_FILTER_SEARCH_PARAMS = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // ── DJ: Sync pill checked states after grid replacement ──
    FacetFiltersForm.syncPills();

    // ── DJ: Re-init product card pickers ──
    if (typeof window.initDJPickers === 'function') window.initDJPickers();
    document.dispatchEvent(new CustomEvent('dj:filters:updated', { detail: { searchParams: FacetFiltersForm.searchParamsPrev || window.location.search.slice(1) } }));
  }

  // ── DJ: Sync all pill inputs to match current URL params ──
  static syncPills() {
    const form = document.getElementById('FacetPillsForm');
    if (!form) return;

    const sourceParams = FacetFiltersForm.searchParamsPrev || window.location.search.slice(1) || '';
    const params = new URLSearchParams(sourceParams);
    const norm = (value) => decodeURIComponent(String(value || '')).trim().toLowerCase();
    const sizeKey = (value) => {
      const v = norm(value).replace(/\s+/g, ' ').replace(/_/g, '-');
      const compact = v.replace(/\s+/g, '').replace(/-/g, '');
      if (['xs', 'xsmall', 'extrasmall'].includes(compact)) return 'xs';
      if (['s', 'small'].includes(compact)) return 's';
      if (['m', 'medium'].includes(compact)) return 'm';
      if (['l', 'large'].includes(compact)) return 'l';
      if (['xl', 'xlarge', 'extralarge'].includes(compact)) return 'xl';
      if (['xxl', '2xl', 'xxlarge', '2xlarge'].includes(compact)) return 'xxl';
      if (['xxxl', '3xl', 'xxxlarge', '3xlarge'].includes(compact)) return 'xxxl';
      return compact;
    };

    const activeSizes = params.getAll('filter.v.option.size').map(norm);
    const activeTypes = params.getAll('filter.p.product_type').map(norm);
    const activeColours = params
      .getAll('filter.v.option.color')
      .concat(params.getAll('filter.v.option.colour'))
      .map(norm);
    const activeBrands = params
      .getAll('filter.p.vendor')
      .concat(params.getAll('filter.v.vendor'))
      .concat(params.getAll('filter.p.m.vendor'))
      .map(norm);

    const activeAvailability = params.getAll('filter.v.availability').includes('1');

    const setCheckedAndActive = (input, active) => {
      input.checked = !!active;
      const label = input.nextElementSibling;
      if (label) label.classList.toggle('is-active', !!active);
    };

    const labelText = (input) => {
      const label = input.nextElementSibling;
      return label ? norm(label.textContent) : '';
    };

    const activeSizeKeys = activeSizes.map(sizeKey);
    form.querySelectorAll('.dj-size-pill-input').forEach((input) => {
      const label = input.nextElementSibling;
      const sizeName = label && label.querySelector('[data-size-name]') ? label.querySelector('[data-size-name]').textContent : labelText(input);
      const isActive = activeSizes.includes(norm(input.value)) || activeSizes.includes(labelText(input)) || activeSizeKeys.includes(sizeKey(input.value)) || activeSizeKeys.includes(sizeKey(sizeName));
      setCheckedAndActive(input, isActive);
    });

    form.querySelectorAll('[data-dj-type-pill]').forEach((input) => {
      const isActive = activeTypes.includes(norm(input.value)) || activeTypes.includes(labelText(input));
      setCheckedAndActive(input, isActive);
    });

    form.querySelectorAll('[data-dj-colour-swatch]').forEach((input) => {
      const isActive = activeColours.includes(norm(input.value)) || activeColours.includes(labelText(input));
      setCheckedAndActive(input, isActive);
    });

    form.querySelectorAll('[data-dj-brand-pill]').forEach((input) => {
      const isActive = activeBrands.includes(norm(input.value)) || activeBrands.includes(labelText(input));
      setCheckedAndActive(input, isActive);
    });

    const availabilityInput = form.querySelector('#DJAvailabilityInput');
    if (availabilityInput) {
      availabilityInput.checked = activeAvailability;
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingCount = parsed.getElementById('ProductCount');
    const count = incomingCount ? incomingCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container && count) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop && count) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // ── DJ: Refresh custom pill form from the server response ──
    // Keep the existing form element so the FacetFiltersForm input listener remains attached.
    const incomingPillsForm = parsedHTML.getElementById('FacetPillsForm');
    const currentPillsForm = document.getElementById('FacetPillsForm');

    if (incomingPillsForm && currentPillsForm) {
      currentPillsForm.innerHTML = incomingPillsForm.innerHTML;
      FacetFiltersForm.syncPills();
    }

    // ── DJ: Refresh our custom active filter chips after AJAX renders ──
    const incomingActiveSlot = parsedHTML.getElementById('DJActiveFilterSlot');
    const currentActiveSlot = document.getElementById('DJActiveFilterSlot');
    if (incomingActiveSlot && currentActiveSlot) {
      currentActiveSlot.innerHTML = incomingActiveSlot.innerHTML;
    }

    // ── DJ: Refresh app-like accordions / size hints after pills are replaced ──
    if (typeof window.DJCollectionFiltersInit === 'function') {
      window.DJCollectionFiltersInit();
    }

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement || !document.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const incoming = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (!incoming || !current) return;
      current.innerHTML = incoming.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  // ── DJ: On mobile, the drawer excludes the custom Type/Size/Colour pills.
  // Preserve those existing URL filters when a drawer filter is added.
  createMergedMobileSearchParams(form) {
    const currentParams = new URLSearchParams(window.location.search.slice(1) || FacetFiltersForm.searchParamsPrev || '');
    const formData = new FormData(form);
    const formParams = new URLSearchParams(formData);
    const formInputNames = new Set();

    form.querySelectorAll('input[name], select[name]').forEach((input) => {
      formInputNames.add(input.name);
    });

    formInputNames.forEach((name) => currentParams.delete(name));

    formParams.forEach((value, key) => {
      if (value !== '') currentParams.append(key, value);
    });

    return currentParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(FacetFiltersForm.enforceAutoAvailability(searchParams), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const sourceForm = event.target.closest('form');
    const isMobile = sourceForm && sourceForm.id === 'FacetFiltersFormMobile';

    if (isMobile) {
      const searchParams = this.createMergedMobileSearchParams(sourceForm);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' || form.id === 'FacetPillsForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

const djInitialSearchParams = window.location.search.slice(1);
const djEnforcedInitialSearchParams = FacetFiltersForm.enforceAutoAvailability(djInitialSearchParams);

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = djEnforcedInitialSearchParams;
FacetFiltersForm.searchParamsPrev = djEnforcedInitialSearchParams;
customElements.define('facet-filters-form', FacetFiltersForm);
window.FacetFiltersForm = FacetFiltersForm;
window.DJ_LAST_FILTER_SEARCH_PARAMS = djEnforcedInitialSearchParams;
FacetFiltersForm.setListeners();

// DJ: make custom pills reflect filtered landing URLs immediately.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.syncPills());
} else {
  FacetFiltersForm.syncPills();
}
window.addEventListener('pageshow', () => FacetFiltersForm.syncPills());

// If a customer lands on a filtered URL without availability, normalise it immediately.
if (djEnforcedInitialSearchParams !== djInitialSearchParams) {
  FacetFiltersForm.renderPage(djEnforcedInitialSearchParams);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);